import React from 'react'
import { Well, EveryNSchedule, EveryNScheduleType, IEveryNScheduleValue, IDayPartValue, DayPartSelect, ALL_SCHEDULE_DAYS } from '@pushly/aqe/lib/components'
import '@ant-design/compatible/assets/index.css'
import { Radio, Select, Tooltip } from 'antd'
import { WarningOutlined } from '@ant-design/icons'
import moment from 'moment-timezone'
import { ISchedulingDatetimePickerValue, SchedulingDatetimePicker } from '../../../../components/scheduling-datetime-picker/scheduling-datetime-picker'

import type { DomainDto } from '../../../../dtos/domain'
import type { IRecurrenceSchedule, ScheduleRelativeType } from '@pushly/models/lib/interfaces/recurrence-schedule'
import { buildAllowedDayMap } from '../../utils/build-allowed-day-map'

export interface IRecurrenceSectionProps {
  schedule: IRecurrenceSchedule
  editable: boolean
  domain: DomainDto
  journeyTimeZone: string
  onUpdateSchedule: (mutate: (s: IRecurrenceSchedule) => void) => void
}

type ScheduleSuperType = 'daily' | 'weekly' | 'monthly'

export const RecurrenceSection: React.FC<IRecurrenceSectionProps> = React.memo(({ schedule, editable, domain, journeyTimeZone, onUpdateSchedule }) => {
  const type = schedule.type
  const relativeType = schedule.relative_type
  const scheduleType = (relativeType ?? type) as string
  const scheduleSuperType: ScheduleSuperType = (type || 'daily').replace(/_relative|_specific/, '') as ScheduleSuperType

  const everyNValue: IEveryNScheduleValue = {
    type: scheduleSuperType as EveryNScheduleType,
    frequency: schedule.frequency ?? 1,
    allowed_days: schedule.allowed_days ?? [],
  }
  const specificDay = schedule.specific_day ?? 1
  const relativeDayValue = schedule.relative_day?.day
  const relativeDayOrdinal = schedule.relative_day?.ordinal
  const pickerTimeZone = schedule.time_zone
  const specificTime = schedule.specific_time

  const handleScheduleTypeChange = (nextSuper: ScheduleSuperType) => {
    onUpdateSchedule((s) => {
      if (nextSuper === 'monthly') {
        s.type = 'monthly_specific'
        s.specific_day = 1
        return
      }

      // Reset frequency when leaving monthly
      const wasMonthly = s.type?.startsWith('monthly')
      if (wasMonthly) {
        s.frequency = 1
      }

      s.type = nextSuper
      s.allowed_days = nextSuper === 'daily'
        ? ALL_SCHEDULE_DAYS
        : [moment().format('dddd').toLowerCase()]
    })
  }

  const handleMonthlyTypeChange = (mt: 'monthly_specific' | ScheduleRelativeType) => {
    onUpdateSchedule((s) => {
      if (mt === 'monthly_specific') {
        s.specific_day = 1
        s.relative_type = undefined
        s.type = 'monthly_specific'
      } else {
        s.relative_type = mt
        if (mt === 'relative_day') {
          s.relative_day = { ordinal: 'first', day: s.relative_day?.day ?? '' }
        }
        s.type = 'monthly_relative'
      }
    })
  }

  const handleEveryNChange = (value: IEveryNScheduleValue | undefined) => {
    onUpdateSchedule((s) => {
      s.frequency = value?.frequency
      s.allowed_days = value?.allowed_days
    })
  }

  const handleSpecificDayChange = (day: number) => {
    onUpdateSchedule((s) => { s.specific_day = day })
  }

  const handleRelativeDayOrdinalChange = (ordinal: string) => {
    onUpdateSchedule((s) => {
      s.relative_day = { ordinal, day: s.relative_day?.day ?? '' }
    })
  }

  const handleRelativeDayValueChange = (days: IDayPartValue) => {
    const selected = Object.keys(days).filter((d) => Boolean(days[d as keyof IDayPartValue]))
    onUpdateSchedule((s) => {
      s.relative_day = selected.length > 0
        ? { ordinal: s.relative_day?.ordinal ?? 'first', day: selected[0] }
        : undefined
    })
  }

  const handleTimeChange = (val: ISchedulingDatetimePickerValue) => {
    onUpdateSchedule((s) => {
      s.time_zone = val.timeZone
      s.specific_time = val.time?.format('HH:mm')
    })
  }

  return (
    <div className="recurrence-schedule-builder">
      <Well mode="ghost" title="Schedule Type" hideFooter={true}>
        <div className="schedule-type-row">
          <div className="schedule-type-wrapper">
            <Select
              className="journey-trigger-editor-rec-schedule-type-select"
              size="small"
              disabled={!editable}
              value={scheduleSuperType}
              onChange={handleScheduleTypeChange}
            >
              <Select.Option value="daily">Daily</Select.Option>
              <Select.Option value="weekly">Weekly</Select.Option>
              <Select.Option value="monthly">Monthly</Select.Option>
            </Select>
          </div>
          <div className="monthly-type-wrapper">
            {/^(monthly|first_day|last_day|relative_day)/.test(scheduleType) && scheduleSuperType === 'monthly' && (
              <Radio.Group
                size="small"
                className="journey-trigger-editor-rec-schedule-monthly-type-select"
                disabled={!editable}
                value={scheduleType}
                onChange={(e) => handleMonthlyTypeChange(e.target.value)}
              >
                <Tooltip title="The journey will run once a month on the chosen day number of the month.">
                  <Radio.Button value="monthly_specific">Specific Day</Radio.Button>
                </Tooltip>
                <Tooltip title="The journey will run once a month on the first day of the month.">
                  <Radio.Button value="first_day">First Day</Radio.Button>
                </Tooltip>
                <Tooltip title="The journey will run once a month on the last day of the month.">
                  <Radio.Button value="last_day">Last Day</Radio.Button>
                </Tooltip>
                <Tooltip title="The journey will run once a month on the chosen relative date of the month.">
                  <Radio.Button value="relative_day">Relative Day</Radio.Button>
                </Tooltip>
              </Radio.Group>
            )}
          </div>
        </div>
      </Well>

      {scheduleSuperType !== 'monthly' && (
        <Well mode="ghost" title={`${scheduleSuperType === 'daily' ? 'Daily' : 'Weekly'} Schedule`} hideFooter={true}>
          <EveryNSchedule
            key={scheduleSuperType}
            type={scheduleSuperType as EveryNScheduleType}
            disabled={!editable}
            value={everyNValue}
            onChange={handleEveryNChange}
          />
        </Well>
      )}

      {scheduleType === 'monthly_specific' && (
        <Well mode="ghost" title="Specific Day" hideFooter={true}>
          <Select
            className="specific-day-select"
            size="small"
            disabled={!editable}
            defaultValue={1}
            value={specificDay ?? 1}
            onChange={handleSpecificDayChange}
          >
            {Array.from({ length: 31 }).map((_, idx) => {
              const day = idx + 1
              return (
                <Select.Option key={day} value={day}>
                  {day}
                </Select.Option>
              )
            })}
          </Select>
          {(specificDay ?? 1) > 28 && (
            <div className="form-layout-row specific-day-warning">
              <div className="form-layout-row-sub">
                <WarningOutlined />
                <span>
                  This journey will not run in months that do not have the chosen number of days. To always target the last day of the month use the "Relative Day" option.
                </span>
              </div>
            </div>
          )}
        </Well>
      )}

      {scheduleType === 'relative_day' && (
        <Well mode="ghost" title="Relative Day" hideFooter={true}>
          <div className="relative-day-wrapper">
            <Select
              className="relative-day-ordinal-select"
              size="small"
              disabled={!editable}
              value={relativeDayOrdinal}
              onChange={handleRelativeDayOrdinalChange}
            >
              <Select.Option value="first">First</Select.Option>
              <Select.Option value="second">Second</Select.Option>
              <Select.Option value="third">Third</Select.Option>
              <Select.Option value="fourth">Fourth</Select.Option>
              <Select.Option value="fifth">Fifth</Select.Option>
            </Select>

            <DayPartSelect
              className="relative-day-select"
              size="small"
              mode="single"
              disabled={!editable}
              daysOnly={true}
              dayFormat={'ddd'}
              weekBeginsOnSunday={true}
              defaultValue={buildAllowedDayMap(relativeDayValue ? [relativeDayValue] : [])}
              value={buildAllowedDayMap(relativeDayValue ? [relativeDayValue] : [])}
              onChange={handleRelativeDayValueChange}
            />
          </div>
        </Well>
      )}

      <Well mode="ghost" title="Schedule Time" hideFooter={true}>
        <SchedulingDatetimePicker
          size="small"
          mode="time"
          domain={domain}
          disabled={!editable}
          timeZone={journeyTimeZone}
          hideTimeZoneOptions={true}
          value={{
            timeZone: pickerTimeZone,
            time: !specificTime ? undefined : moment(specificTime, 'HH:mm'),
          }}
          onChange={handleTimeChange}
        />
      </Well>
    </div>
  )
})
